// Баланс анимируется отдельно от переключения обложки карты.
import { IconButton } from '@mobile/components/kartoy/icon-button'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import type { useCardSheet } from '@mobile/screens/card-details/use-card-sheet'
import { useAuthorizationGate } from '@shared-core/hooks/use-authorization-gate'
import { useCardOperationAvailability } from '@shared-core/hooks/use-card-operation-availability'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { useWithdrawAvailability } from '@shared-core/hooks/use-withdraw-availability'
import { ScreensList, useNavigation } from '@shared-core/navigation'
import { AppAnimatedBalance } from '@shared-ui/components/app/animated-balance'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Column, Flex, Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useAppTheme } from '@shared-ui/theme'
import { rh, rw } from '@shared-ui/theme/scale'
import { Animated, Pressable } from 'react-native'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export type CardSummaryProps = { cardId?: string; balance: string; balanceKey: string; onToggleBalance: () => void }

export function CardSummary({ cardId, balance, balanceKey, onToggleBalance }: CardSummaryProps) {
  const styles = useCreateStyles()
  return (
    <Column gap={32} style={styles.summary}>
      <Pressable accessibilityRole="button" onPress={onToggleBalance}>
        <AppAnimatedBalance balance={balance} balanceKey={balanceKey} />
      </Pressable>
      <CardBalanceActions cardId={cardId} />
    </Column>
  )
}

export function CardBalanceActions({ cardId }: { cardId?: string }) {
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const { ensureAuthorized } = useAuthorizationGate({ requireEmail: true })
  const ensureWithdrawAvailable = useWithdrawAvailability()
  const ensureCardOperationAvailable = useCardOperationAvailability()
  return (
    <Row gap={16}>
      <Flex>
        <PillButton
          title={t('topup')}
          icon="topup"
          onPress={() => {
            if (ensureAuthorized(true) && ensureCardOperationAvailable('topup', cardId))
              navigation.navigate(ScreensList.TOPUP_AMOUNT, { cardId })
          }}
        />
      </Flex>
      <Flex>
        <PillButton
          title={t('withdraw')}
          icon="withdraw"
          variant="secondary"
          onPress={async () => {
            if (!ensureAuthorized(true) || !ensureCardOperationAvailable('withdraw', cardId)) return

            if (!(await ensureWithdrawAvailable())) return

            navigation.navigate(ScreensList.FORM_SHEET_WITHDRAW_BANK, { cardId })
          }}
        />
      </Flex>
    </Row>
  )
}

type CardHeaderProps = {
  balance: string
  balanceKey: string
  sheet: Pick<ReturnType<typeof useCardSheet>, 'detailsOpacity' | 'panelTop'>
  isHome: boolean
  onBack?: () => void
  onToggleBalance: () => void
}

export function CardHeader({ balance, balanceKey, sheet, isHome, onBack, onToggleBalance }: CardHeaderProps) {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const operationsTitleOpacity = sheet.panelTop.interpolate({
    inputRange: [0, rh(100)],
    outputRange: [1, 0],
    extrapolate: 'clamp',
  })
  const leading = isHome ? (
    <AppIcon icon="profile" size={rw(40)} color={theme.COLOR_TEXT_TERTIARY} hasCircle={false} />
  ) : (
    <AppIcon icon="chevron-left" size={24} />
  )
  return (
    <Row align="center" justify="space-between" style={styles.header}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('back')}
        onPress={() => {
          if (onBack) onBack()
          else if (isHome) navigation.navigate(ScreensList.FORM_SHEET_PROFILE)
          else if (navigation.canGoBack()) navigation.goBack()
          else navigation.navigate(ScreensList.HOME_START)
        }}
        style={styles.headerButton}
      >
        {leading}
      </Pressable>
      <Animated.View style={{ opacity: sheet.detailsOpacity }}>
        <Pressable accessibilityRole="button" onPress={onToggleBalance}>
          <AppAnimatedBalance balance={balance} balanceKey={balanceKey} isCompact />
        </Pressable>
      </Animated.View>
      <Animated.View pointerEvents="none" style={[styles.operationsHeaderTitle, { opacity: operationsTitleOpacity }]}>
        <GText fw={600} style={styles.operationsHeaderText}>
          {t('operations')}
        </GText>
      </Animated.View>
      <IconButton icon="help" size={40} onPress={() => navigation.navigate(ScreensList.FORM_SHEET_FAQ)} />
    </Row>
  )
}
